// Яндекс.Метрика витрины: электронная коммерция, цели и просмотры SPA
// (fd-analytics-05).
//
// Электронная коммерция - через `window.dataLayer` в формате Метрики:
// `detail` на странице товара, `add` / `remove` в корзине, `purchase` на
// странице успеха с номером заказа, суммой и позициями. Счётчик читает
// контейнер сам (параметр `ecommerce: "dataLayer"` при инициализации), поэтому
// события, отправленные до загрузки тега, не теряются: тег разберёт накопленное.
//
// Цели - `ym(counter, "reachGoal", <идентификатор>)`. Идентификаторы закрытым
// списком (`METRIKA_GOALS`): ровно такие же цели «JavaScript-событие» нужно
// завести в счётчике, это делает кнопка в консоли или руками в Метрике. Пока
// номер счётчика не пришёл, цели копятся в очереди; если счётчика нет вовсе,
// очередь сбрасывается и ничего не уходит.
//
// Сбой Метрики (блокировщик рекламы, нет сети) витрину не ломает: каждый вызов
// обёрнут и молча гасится.

export const METRIKA_GOALS = {
	registration: "registration",
	promoApplied: "promo_applied",
	checkout: "checkout",
	payment: "payment",
	becomeSeller: "become_seller",
} as const;
export type MetrikaGoal = (typeof METRIKA_GOALS)[keyof typeof METRIKA_GOALS];

/** Позиция электронной коммерции - поля, которые понимает Метрика. */
export type EcommerceProduct = {
	id: string;
	name: string;
	price: number;
	brand?: string;
	category?: string;
	variant?: string;
	quantity?: number;
};

type YmFn = (...args: unknown[]) => void;
type MetrikaGlobal = { dataLayer?: unknown[]; ym?: YmFn };

const CURRENCY = "RUB";

const win = (): MetrikaGlobal | null =>
	typeof window === "undefined" ? null : (window as unknown as MetrikaGlobal);

/** Контейнер электронной коммерции; создаётся при первом обращении. */
export const dataLayer = (): unknown[] => {
	const w = win();
	if (!w) return [];
	if (!Array.isArray(w.dataLayer)) w.dataLayer = [];
	return w.dataLayer;
};

let counterId: number | null = null;
/** false - номер счётчика ещё не пришёл; true и `counterId: null` - счётчика нет. */
let counterKnown = false;
const pendingGoals: { goal: MetrikaGoal; params?: Record<string, unknown> }[] = [];

const ym = (...args: unknown[]) => {
	try {
		win()?.ym?.(...args);
	} catch {
		// Метрика - не повод ронять витрину.
	}
};

/**
 * Номер счётчика стал известен (`use-metrika`). `null` - счётчик не задан:
 * накопленные цели выбрасываются.
 */
export const setMetrikaCounter = (id: string | null): void => {
	counterKnown = true;
	counterId = id && /^\d+$/.test(id) ? Number(id) : null;
	const queued = pendingGoals.splice(0);
	if (counterId == null) return;
	for (const g of queued) ym(counterId, "reachGoal", g.goal, g.params);
};

/** Достижение цели. До загрузки номера счётчика - в очередь. */
export const reachGoal = (goal: MetrikaGoal, params?: Record<string, unknown>): void => {
	if (!counterKnown) {
		pendingGoals.push({ goal, params });
		return;
	}
	if (counterId != null) ym(counterId, "reachGoal", goal, params);
};

/** Просмотр страницы при переходе внутри SPA - первый счётчик считает сам. */
export const metrikaHit = (url: string, referer?: string, title?: string): void => {
	if (counterId == null) return;
	ym(counterId, "hit", url, { ...(referer ? { referer } : {}), ...(title ? { title } : {}) });
};

const push = (action: "detail" | "add" | "remove", products: EcommerceProduct[]): void => {
	const clean = products.filter((p) => p.id && Number.isFinite(p.price));
	if (clean.length === 0) return;
	dataLayer().push({ ecommerce: { currencyCode: CURRENCY, [action]: { products: clean } } });
};

/** Просмотр карточки товара. */
export const ecDetail = (product: {
	id: string;
	name: string;
	price: number;
	brand?: string;
	category?: string;
}): void =>
	push("detail", [
		{
			id: product.id,
			name: product.name,
			price: product.price,
			...(product.brand ? { brand: product.brand } : {}),
			...(product.category ? { category: product.category } : {}),
		},
	]);

/** Строка корзины в позицию Метрики. */
export const fromCartItem = (
	item: { productId: string; name: string; brand?: string; unitPrice: number; size?: string; color?: string; variantLabel?: string },
	quantity: number,
): EcommerceProduct => {
	const variant = item.variantLabel || [item.color, item.size].filter(Boolean).join(", ");
	return {
		id: item.productId,
		name: item.name,
		price: item.unitPrice,
		...(item.brand ? { brand: item.brand } : {}),
		...(variant ? { variant } : {}),
		quantity,
	};
};

/** Добавление в корзину. */
export const ecAdd = (products: EcommerceProduct[]): void => push("add", products);
/** Удаление из корзины. */
export const ecRemove = (products: EcommerceProduct[]): void => push("remove", products);

type CartLike = {
	items: {
		id: string;
		productId: string;
		name: string;
		brand?: string;
		unitPrice: number;
		qty: number;
		size?: string;
		color?: string;
		variantLabel?: string;
	}[];
};

/**
 * Разница двух состояний корзины в события `add` и `remove`: так одна функция
 * обслуживает «В корзину», смену количества и удаление, не зная, что из этого
 * произошло.
 */
export const ecCartDiff = (before: CartLike | null | undefined, after: CartLike | null | undefined): void => {
	const prev = new Map((before?.items ?? []).map((i) => [i.id, i]));
	const next = new Map((after?.items ?? []).map((i) => [i.id, i]));
	const added: EcommerceProduct[] = [];
	const removed: EcommerceProduct[] = [];
	for (const [id, item] of next) {
		const delta = item.qty - (prev.get(id)?.qty ?? 0);
		if (delta > 0) added.push(fromCartItem(item, delta));
		else if (delta < 0) removed.push(fromCartItem(item, -delta));
	}
	for (const [id, item] of prev) if (!next.has(id)) removed.push(fromCartItem(item, item.qty));
	ecAdd(added);
	ecRemove(removed);
};

const SENT_KEY = "osmix-metrika-sent";

/** Уже отправляли ли это событие по этому заказу в этой вкладке; отметить. */
const alreadySent = (key: string): boolean => {
	try {
		const raw = win() ? window.sessionStorage.getItem(SENT_KEY) : null;
		const keys: string[] = raw ? JSON.parse(raw) : [];
		if (keys.includes(key)) return true;
		window.sessionStorage.setItem(SENT_KEY, JSON.stringify([...keys, key].slice(-40)));
		return false;
	} catch {
		return false;
	}
};

/** Цель один раз на заказ: «Оплата» не задваивается при возврате на страницу успеха. */
export const reachGoalOnce = (goal: MetrikaGoal, orderId: string): void => {
	if (!orderId || alreadySent(`${goal}:${orderId}`)) return;
	reachGoal(goal, { order_id: orderId });
};

/**
 * Покупка. Один заказ - одна покупка: страница успеха может открыться дважды
 * (обновили вкладку, вернулись из платёжной системы), повтор не уходит.
 */
export const ecPurchase = (order: { id: string; revenue?: number; products: EcommerceProduct[] }): void => {
	if (!order.id || order.products.length === 0 || alreadySent(`purchase:${order.id}`)) return;
	const revenue =
		order.revenue ?? order.products.reduce((s, p) => s + p.price * (p.quantity ?? 1), 0);
	dataLayer().push({
		ecommerce: {
			currencyCode: CURRENCY,
			purchase: {
				actionField: { id: order.id, revenue: Math.round(revenue * 100) / 100 },
				products: order.products,
			},
		},
	});
};

type OrderLike = {
	id: string;
	total: number;
	groups: {
		items: {
			productId: string;
			name: string;
			brand?: string;
			unitPrice: number;
			qty: number;
			size?: string;
			color?: string;
			variantLabel?: string;
		}[];
	}[];
};

/** Покупка по снимку заказа шлюза: позиции всех магазинов, сумма к оплате. */
export const ecPurchaseFromOrder = (order: OrderLike): void =>
	ecPurchase({
		id: order.id,
		revenue: order.total,
		products: order.groups.flatMap((g) => g.items.map((it) => fromCartItem(it, it.qty))),
	});

/** Только для тестов: вернуть модуль в исходное состояние. */
export const __resetMetrikaForTests = (): void => {
	counterId = null;
	counterKnown = false;
	pendingGoals.splice(0);
};
